/**
 * ArrivalVerifyDialog — Ted #28 (#1050), "most importantly".
 *
 * Ted: "when the dog appears for check-in, the transportation flag should be
 * clearly visible to the staff member checking dogs in and should trigger
 * them to verify that the dog is physically at the facility before
 * completing check-in. I do not want a transported dog to simply be assumed
 * to have arrived because it appears on that day's reservations."
 *
 * The SERVER is the gate: `POST …/check-in` on a transported arrival answers
 * 409 `arrival_unverified` until the body carries `arrival_verified: true`.
 * This dialog is how a human supplies that answer, and it opens only in
 * reply to that refusal — so a surface that forgets it still cannot check
 * the dog in silently, it just shows the server's sentence instead. Every
 * check-in surface (Front Desk, Dashboard arrivals, Bookings) mounts it via
 * `useArrivalVerification`, one request at a time: a family with two
 * transported dogs gets two questions, one per dog, never one blanket yes
 * (Ted #26's "never removing staff control").
 *
 * Every exit is explicit. Escape and "Not here yet" leave the dog checked
 * OUT — nothing about this dialog checks a dog in but the primary button.
 */
import { useEffect, useRef } from 'react';

export interface ArrivalVerifyRequest {
  bookingId: string;
  dogNames: readonly string[];
  /** Runs after the desk confirms — the caller re-POSTs with the flag. */
  onConfirm: () => void | Promise<void>;
  /** Runs when the desk declines (or presses Escape). Optional. */
  onCancel?: () => void;
}

interface ArrivalVerifyDialogProps {
  request: ArrivalVerifyRequest;
  onConfirm: () => void;
  onCancel: () => void;
}

function joinNames(names: readonly string[]): string {
  if (names.length === 0) return 'this dog';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

export function ArrivalVerifyDialog({
  request,
  onConfirm,
  onCancel,
}: ArrivalVerifyDialogProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const who = joinNames(request.dogNames);
  const plural = request.dogNames.length > 1;

  // Focus lands on the SAFE choice: Enter alone never checks a dog in.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="arrival-verify-title"
      aria-describedby="arrival-verify-body"
      data-testid="arrival-verify-dialog"
      data-booking-id={request.bookingId}
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onCancel();
      }}
    >
      <div className="ferro-blocking-dialog__panel ferro-arrival-verify">
        <p className="ferro-arrival-verify__eyebrow">Transport arrival</p>
        <h2 id="arrival-verify-title">
          {plural ? `Are ${who} here?` : `Is ${who} here?`}
        </h2>
        <p id="arrival-verify-body">
          {plural ? 'These dogs arrive' : 'This dog arrives'} by transport, so being on
          today&rsquo;s list does not mean {plural ? 'they have' : 'it has'} arrived.
          Check-in is not complete until someone confirms {who} {plural ? 'are' : 'is'}{' '}
          physically at the facility. Your name and the time are recorded.
        </p>
        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            ref={cancelRef}
            onClick={onCancel}
            data-testid="arrival-verify-cancel"
          >
            Not here yet
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={onConfirm}
            data-testid="arrival-verify-confirm"
          >
            {plural ? `Yes, they're here — check in` : `Yes, ${who} is here — check in`}
          </button>
        </div>
      </div>
    </div>
  );
}
